<template>
  <div class="item-search-page-section">
    <MiddleCategoryWidget
      v-if="visibleMiddleCategoryWidget"
      :largeCategoryCd="largetCategoryCdComputed"
      v-model:middleCategoryCd="middleCategoryCdComputed"
      v-model:selectedDiameters="selectedDiametersValue"
      v-model:selectedLengths="selectedLengthsValue"
      v-model:selectedSmallCategoryCds="selectedSmallCategoryCdsValue"
      v-model:hasStockOnly="selectedHasStockOnlyValue"
      @update:middle-category="setMiddleCategory"
    />
    <template v-if="items && items.length > 0">
      <ItemListBlock
        class="item-list-block"
        :items="items"
        :diameter="diameter" 
        :length="length" 
        :stock="stock"
        id="tour-01001-step3"
      />
      <div class="text-center mt-5">
        <v-pagination
          v-model="page"
          :length="calculateTotalPage()"
          :total-visible="5"
        ></v-pagination>
      </div>
    </template>
    <template v-else>
      <v-container v-if="searchItemsLoading">
        商品情報を取得中です。
      </v-container>
      <v-container v-if="!searchItemsLoading">
        商品情報が見つかりませんでした。
      </v-container>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, toRefs, computed, onMounted } from 'vue'
import ItemListBlock from '@/components/molecules/blocks/ItemListBlock.vue'
import MiddleCategoryWidget from '@/components/organisms/widgets/MiddleCategoryWidget.vue'
import * as api from '@/api/okaso-api'
import { useRoute } from 'vue-router'
import { SearchItemCondition, useSearchItemsFunc } from '@/services/searchItems'
import router from '@/router'
import { useUrlFunc } from '@/utils/url'
const url = useUrlFunc()

interface Emits {
  (e: 'update:middleCategory', middleCategory?: api.MiddleCategory): void,
}
const emits = defineEmits<Emits>()

interface Props {
  largeCategoryCd?: number;
  middleCategoryCd?: number;
  keyword?: string;
}

const props = withDefaults(defineProps<Props>(), {
  largeCategoryCd: undefined,
  middleCategoryCd: undefined,
  keyword: undefined,
})

const page = ref<number>()
const perPage = 24
const totalCount = ref<number>(0)

const route = useRoute()

// 径
const diameter = ref<string>()
// 長さ
const length = ref<string>()
// 在庫
const stock = ref<boolean>()

const visibleMiddleCategoryWidget = ref<boolean>(false)

const { largeCategoryCd, middleCategoryCd, keyword } = toRefs(props);
const largetCategoryCdComputed = computed({
  get: () => largeCategoryCd.value,
  set: (value) => largeCategoryCd.value = value
})

const middleCategoryCdComputed = computed({
  get: () => middleCategoryCd.value,
  set: (value) => middleCategoryCd.value = value
})

const keywordComputed = computed({
  get: () => keyword.value,
  set: (value) => keyword.value = value
})

const selectedDiametersValue = ref<string[]>([])
const selectedLengthsValue = ref<string[]>([])
const selectedSmallCategoryCdsValue = ref<number[]>([])
const selectedHasStockOnlyValue = ref<boolean>(false)

// 商品・商品トータル件数取得 ------------------
const items = ref<api.Item[]>()
const {
  searchItemsCount,
  searchItemsCountOnResult,
  searchItems,
  searchItemsOnError,
  searchItemsOnResult,
  searchItemsLoading,
} = useSearchItemsFunc()

searchItemsOnError((error) => {
  console.log(error.message)
})

searchItemsCountOnResult((result) => {
  totalCount.value = result.data?.aggregateItem?._count?._all as number
})  

searchItemsOnResult((result) => {
  if (result.data?.items || result.data?.items.length > 0) {
    items.value = result.data?.items as api.Item[]
  } else {
    items.value = []
  }
})

const searchItemsExt = (condition: SearchItemCondition) => {
  // 商品検索
  searchItemsCount(condition)
  searchItems(condition, perPage, calculateSkip())
}

searchItemsOnError((error) => {
  console.log(error.message)
})

/// -------------
const calculateTotalPage = () => {
  if (page.value && totalCount.value) {
    return Math.ceil(totalCount.value / perPage)
  }else{
    return 0
  }
}

const calculateSkip = () => {
  if (page.value === undefined) {
    return 0
  }
  return (page.value - 1)*perPage
}

const setMiddleCategory = (middleCat?: api.MiddleCategory) => {
  emits('update:middleCategory', middleCat)
}

// -----------------------------

onMounted(() => {
  if (largetCategoryCdComputed.value) {
    visibleMiddleCategoryWidget.value = true
  } else {
    visibleMiddleCategoryWidget.value = false
  }
  // page.value = 1
  if (route.query['page']) {
    if (!Array.isArray(route.query['page'])) {
      page.value = parseInt(route.query['page'], 10)
    } 
  }else{
    page.value = 1
  }
  // 商品再取得
  searchItemsExt({
    largeCategoryCd: largetCategoryCdComputed.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    keyword: keyword.value
  })
})

// route変更時の処理
watch(route, () => {
  if (route.query['page']) {
    if (!Array.isArray(route.query['page'])) {
      page.value = parseInt(route.query['page'], 10)
    } 
  }else{
    page.value = 1
  }
  switch(route.name) { 
    case 'items':
      visibleMiddleCategoryWidget.value = false
      searchItems({
          largeCategoryCd: undefined,
          middleCategoryCd: undefined,
          keyword: keywordComputed.value
        }, perPage, calculateSkip())
      break
    default:
      visibleMiddleCategoryWidget.value = true
      searchItems({
          largeCategoryCd: largetCategoryCdComputed.value,
          middleCategoryCd: middleCategoryCdComputed.value,
          smallCategoryCds: selectedSmallCategoryCdsValue.value,
          diameters: selectedDiametersValue.value,
          lengths: selectedLengthsValue.value,
          hasStockOnly: selectedHasStockOnlyValue.value,
          keyword: keywordComputed.value,
        }, perPage, calculateSkip())
      break
  }
})

watch(largeCategoryCd, () => {
  page.value = 1
  // 商品再取得
  searchItemsExt({
    largeCategoryCd: largetCategoryCdComputed.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    smallCategoryCds: selectedSmallCategoryCdsValue.value,
    diameters: selectedDiametersValue.value,
    lengths: selectedLengthsValue.value,
    hasStockOnly: selectedHasStockOnlyValue.value,
    keyword: keywordComputed.value,
  })
})

// キーワード変更時
watch(keyword, () => {
  page.value = 1
  // 商品再取得
  searchItemsExt({
    largeCategoryCd: largetCategoryCdComputed.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    smallCategoryCds: selectedSmallCategoryCdsValue.value,
    diameters: selectedDiametersValue.value,
    lengths: selectedLengthsValue.value,
    hasStockOnly: selectedHasStockOnlyValue.value,
    keyword: keywordComputed.value,
  })
})

// 中項目変更時の処理
watch( middleCategoryCd, () => {
  page.value = 1
  // 商品再取得
  searchItemsExt({
    largeCategoryCd: largetCategoryCdComputed.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    smallCategoryCds: selectedSmallCategoryCdsValue.value,
    diameters: selectedDiametersValue.value,
    lengths: selectedLengthsValue.value,
    hasStockOnly: selectedHasStockOnlyValue.value,
    keyword: keywordComputed.value,
  })
})

watch(selectedSmallCategoryCdsValue, () => {
  page.value = 1
  // 商品再取得
  searchItemsExt({
    largeCategoryCd: largetCategoryCdComputed.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    smallCategoryCds: selectedSmallCategoryCdsValue.value,
    diameters: selectedDiametersValue.value,
    lengths: selectedLengthsValue.value,
    hasStockOnly: selectedHasStockOnlyValue.value,
    keyword: keywordComputed.value,
  })
})

watch(selectedLengthsValue, () => {
  page.value = 1
  // 商品再取得
  searchItemsExt({
    largeCategoryCd: largetCategoryCdComputed.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    smallCategoryCds: selectedSmallCategoryCdsValue.value,
    diameters: selectedDiametersValue.value,
    lengths: selectedLengthsValue.value,
    hasStockOnly: selectedHasStockOnlyValue.value,
    keyword: keywordComputed.value,
  })
})

watch(selectedDiametersValue, () => {
  page.value = 1
  // 商品再取得
  searchItemsExt({
    largeCategoryCd: largetCategoryCdComputed.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    smallCategoryCds: selectedSmallCategoryCdsValue.value,
    diameters: selectedDiametersValue.value,
    lengths: selectedLengthsValue.value,
    hasStockOnly: selectedHasStockOnlyValue.value,
    keyword: keywordComputed.value,
  })
})

watch(selectedHasStockOnlyValue, () => {
  page.value = 1
  // 商品再取得
  searchItemsExt({
    largeCategoryCd: largetCategoryCdComputed.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    smallCategoryCds: selectedSmallCategoryCdsValue.value,
    diameters: selectedDiametersValue.value,
    lengths: selectedLengthsValue.value,
    hasStockOnly: selectedHasStockOnlyValue.value,
    keyword: keywordComputed.value,
  })
})

watch(page,  () => {
  if (page.value && page.value > 0) {
    if (page.value != 1){
      if (route.name == 'items') {
        router.push({ query: { page: page.value }})
      } else {
        router.push({ query: { page: page.value }})
      }
    }else{
      if (route.name == 'items') {
        router.push({})
      } else {
        router.push({})
      }
    }
    
    
  }
})
</script>

<style lang="scss" scoped>
.item-search-page-section {
  &::v-deep(.item-list-block) {
    margin-left: 14px;
    margin-top: 50px;
  }
}
</style>
